/* eslint-disable padding-line-between-statements */
import { InlineLoader, useModal } from '@jutro/components';
import { BreakpointTrackerContext } from '@jutro/layout';
import { pageTemplateProps, WizardContext } from '@xengage/gw-portals-wizard-react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, {
    useContext,
    useMemo
} from 'react';
import { TranslatorContext } from '@jutro/locale';
import { useHistory } from 'react-router-dom';
import _ from 'lodash'
import { messages as commonMessages } from '@xengage/gw-platform-translations';
import UWCompanyUtil_TWIA from '../utils/UWCompanyUtil_TWIA.js'
import AgentRegistrationSideMenu from './AgentRegistrationSideMenu/AgentRegistrationSideMenu.jsx';
import styles from './AgentRegistrationWizardTemplate.module.scss';
import messages from './AgentRegistrationWizardTemplate.messages.js'

import { Button } from '@jutro/legacy/components';

function renderLoader(isLoading) {
    return isLoading ? (
        <InlineLoader loading={isLoading} className="gw-inline-loader" />
    ) : null;
}

function AgentRegistrationWizardTemplate(props) {
    const translator = useContext(TranslatorContext)
    const breakpoint = useContext(BreakpointTrackerContext);
    const { currentStepIndex, jumpTo, updateWizardData } = useContext(WizardContext);
    const { showConfirm } = useModal();

    const isPhone = breakpoint === 'phoneWide' || breakpoint === 'phone';
    const {
        children: content,
        onNext,
        isLoadingNext,
        disableNext,
        showNext,
        nextLabel,
        onPrevious,
        isLoadingPrevious,
        disablePrevious,
        showPrevious,
        previousLabel,
        isLoadingCancel,
        disableCancel,
        showCancel,
        cancelLabel,
        renderWidgets,
        wizardData
    } = props;
    
    const isTWIA = UWCompanyUtil_TWIA.isTWIACompany(_.get(wizardData, 'uwcompany'))
    const agencyVerification = _.get(wizardData, 'isAgencyVerification.value')
 
    const onCancel = () => {
        showConfirm({
            title: messages.cancelENTModalTitle,
            message: isTWIA ? messages.cancelTWIAModalMessage: messages.cancelTFPAModalMessage,
            status: 'warning',
            icon: 'mi-error-outline',
            confirmButtonText: commonMessages.yesModel,
            cancelButtonText: commonMessages.noModel,
        }).then((results) => {
            if (results === 'cancel' || results === 'close') {
                return _.noop();
            }
            if(currentStepIndex === 0) {
                _.set(wizardData, 'licenseNumber.value', '')
                updateWizardData(wizardData)
            }
            jumpTo(0);
            return true;
        }, _.noop)
    };

    const getLicenseNumber = () => {
        if(currentStepIndex == 0){
            return _.get(wizardData, 'licenseNumber.value')
        } else {
            return _.get(wizardData, 'agencyLicenseNumber.value')
        }
    }

    const getWizardTitle = () => {
        const isAgencyVerification = _.get(wizardData, 'isAgencyVerification.value', false)

        if(isAgencyVerification) {
            if(_.isUndefined(_.get(wizardData, 'uwcompany.value'))) return translator(messages.AgentRegistration)
            if(isTWIA) {
                return translator(messages.TWIAAgentRegistration)
            } 
            return translator(messages.TFPAAgentRegistration)
        }
        if(_.isUndefined(_.get(wizardData, 'uwcompany.value'))) return translator(messages.AgentRegistration)
        if(isTWIA) {
            return translator(messages.TWIAAgentRegistrationLicense, {licenseNumber: getLicenseNumber()})
        } 
        return translator(messages.TFPAAgentRegistrationLicense, {licenseNumber: getLicenseNumber()})
        
    }

    const shouldShowButtons = useMemo(
        () =>
            !(
                showNext === false &&
                showPrevious === false &&
                showCancel === false
            ),
        [showCancel, showNext, showPrevious]
    );

    const isPerformingTransiton = useMemo(
        () => isLoadingCancel || isLoadingPrevious || isLoadingNext,
        [isLoadingCancel, isLoadingNext, isLoadingPrevious]
    );

    const buttonContainerClassName = classNames(styles.navTransitionButtons, {
        [styles.navTransitionButtonOnlyNext]: !showPrevious && isPhone,
    });

    return (
        <div className={styles.digitalWizard}>
            <div
                className={classNames(
                    { [styles.wizardMain]: !agencyVerification }

                )}
            >
                {agencyVerification ? (
                    <div className={styles.wizardWidgets}>
                        {renderWidgets(wizardData)}
                    </div>

                ) :
                    !isPhone ? (
                        <div
                            className={classNames(
                                styles.wizardNavigation,
                                'hiddenPrintElement'
                            )}
                        >
                            <AgentRegistrationSideMenu
                                isTWIA={UWCompanyUtil_TWIA.isTWIACompany(_.get(wizardData, 'uwcompany'))}
                                uwCompany={_.get(wizardData, 'uwcompany.value')}
                            />
                            <div className={styles.wizardWidgets}>
                                {renderWidgets(wizardData)}
                            </div>
                        </div>
                    ) : null
                }
                      
                <div className={styles.wizardContent}>
                <h1 className={styles['gw-page-title']}>{getWizardTitle()}</h1>
                    {content}
                    <div
                        className={classNames(
                            { [styles.navButtons]: shouldShowButtons },
                            'hiddenPrintElement'
                        )}
                    >
                        {showCancel && (
                            <Button
                                id="gw-wizard-cancel"
                                disabled={
                                    disableCancel || isPerformingTransiton
                                }
                                type="text"
                                onClick={onCancel}
                                className={styles.cancelButton}

                            >
                                {translator(cancelLabel)}
                                {renderLoader(isLoadingCancel)}
                            </Button>
                        )}
                        <div className={buttonContainerClassName}>
                            {showPrevious && (
                                <Button
                                    id="gw-wizard-previous"
                                    disabled={
                                        disablePrevious ||
                                        isPerformingTransiton
                                    }
                                    className={classNames(styles.wizardButton, styles.previousbutton)}
                                    onClick={onPrevious}
                                >
                                    {translator(previousLabel)}
                                    {renderLoader(isLoadingPrevious)}
                                </Button>
                            )}
                            {showNext && (
                                <Button
                                    id="gw-wizard-Next"
                                    className={classNames(styles.wizardButton, styles.nextbutton)}
                                    disabled={disableNext || isPerformingTransiton}
                                    onClick={onNext}
                                >
                                    {translator(nextLabel)}
                                    {renderLoader(isLoadingNext)}
                                </Button>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

AgentRegistrationWizardTemplate.propTypes = {
    ...pageTemplateProps,
    /** a function that accepts wizardData to render a React component */
    renderContextComponent: PropTypes.func,
    /** a function that accepts wizardData to render a React component */
    renderWidgets: PropTypes.func,
    showContextComponent: PropTypes.bool,
    showWizardHeader: PropTypes.bool,
    nextButtonTooltip: PropTypes.string,
};

AgentRegistrationWizardTemplate.defaultProps = {
    renderContextComponent: () => null,
    renderWidgets: () => null,
    showContextComponent: true,
    showWizardHeader: false,
    nextButtonTooltip: '',
};

export default AgentRegistrationWizardTemplate;
